<template>
  <div
    v-if="videoPath || videoIsEmbedded || imageUrl"
    class="image-container"
    :class="[
      { container: !fullWidth, 'full-viewport': fullViewport, 'container-shadow': withShadow },
      !fullViewport && backgroundImageUrl
        ? `${externalMargin ? 'external-margin' : 'auto-margin'}`
        : ''
    ]"
    :style="backgroundImageStyle"
  >
    <b-container>
      <b-row :class="{ 'reverse-row': isRight }">
        <b-col :style="imagePosition">
          <Video v-if="videoPath || videoIsEmbedded" :path="videoPath" :options="options" />
          <img
            v-else-if="isGif"
            :src="imageUrl"
            :alt="imageAlt"
            class="image img-fluid"
            loading="lazy"
            width="525"
            height="525"
          />
          <picture v-else>
            <source
              v-for="{ resolution, size } in imageSizes"
              :key="resolution"
              :media="`(max-width: ${resolution}px)`"
              :srcset="`${imageUrl}&w=${size}`"
            />
            <img
              :src="imageUrl"
              :alt="imageAlt"
              class="image img-fluid"
              loading="lazy"
              width="525"
              height="525"
            />
          </picture>
        </b-col>
        <b-col v-if="title || text || (buttonText && buttonPath)" class="text-container">
          <b-row class="text-content" :style="textColorStyle">
            <b-col>
              <prismic-rich-text v-if="title" :field="title" class="title"></prismic-rich-text>
              <prismic-rich-text
                v-if="text"
                :field="text"
                class="text"
                :class="{ 'text-m-0': endDate }"
              ></prismic-rich-text>
              <div v-if="endDate" class="d-block">
                <p class="counter-slice-seasonal">
                  {{ $t('seasonal.main.slice.prefixCounter') }} {{ timeRemaining }}
                </p>
              </div>
              <ButtonActions
                v-if="buttonText"
                :blank="buttonBlank"
                :path="buttonPath"
                :anchor-id-target="anchorIdTarget"
                :open-modal="openModal"
              >
                <template #action-scroll="{ item }">
                  <Button
                    :to="item.path"
                    :blank="item.blank"
                    v-bind="buttonProps"
                    :size="buttonSize"
                    :extra-data="anchorIdTarget ? { event: '' } : {}"
                    @click="handleClick(item.path)"
                  >
                    {{ buttonText }}
                  </Button>
                </template>
              </ButtonActions>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import Button from 'UI/Button';
import ButtonActions from '@/components/Prismic/Landings/Utils/ButtonActions.vue';
import Video from '@/components/Common/Video.vue';
import { msToHMS } from '~/utils/common.js';

export default {
  components: {
    Button,
    ButtonActions,
    Video
  },
  props: {
    slice: {
      type: Object,
      required: true
    },
    isRight: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      buttonBlank: false,
      buttonPath: '',
      buttonText: '',
      imageAlt: '',
      imageUrl: '',
      backgroundImageUrl: '',
      openModal: false,
      text: '',
      textParsed: '',
      timerId: 0,
      timeDiff: 0,
      timeRemaining: '',
      title: '',
      textColor: '#707070',
      videoPath: '',
      videoIsEmbedded: false,
      imageVerticalAlign: '',
      buttonBackgroundTransparent: false,
      buttonTextColor: '',
      buttonBackgroundColor1: '',
      buttonBackgroundColor2: '',
      borderRounded: false,
      internalPadding: true,
      externalMargin: false,
      endDate: '',
      withShadow: false,
      imageSizes: [
        { resolution: 360, size: 280 },
        { resolution: 375, size: 320 },
        { resolution: 414, size: 384 },
        { resolution: 1920, size: 550 }
      ],
      anchorIdTarget: ''
    };
  },
  computed: {
    backgroundImageStyle() {
      const style = {};
      if (this.backgroundImageUrl) {
        style['padding'] = `${this.internalPadding ? '20px 30px' : '0px'}`;
        style['border-radius'] = '5px';
        style['background-size'] = 'cover';
        style['border-radius'] = `${this.borderRounded ? '25px' : '0'}`;
        style['--background-img'] = `url(${this.backgroundImageUrl})`;
        style['--mobile-img'] = `url(${this.mobileBackgroundImageUrl || this.backgroundImageUrl})`;
      }
      return style;
    },
    textColorStyle() {
      return `color: ${this.textColor}`;
    },
    imagePosition() {
      return this.imageVerticalAlign == 'center'
        ? 'margin: auto 0'
        : this.imageVerticalAlign == 'bottom'
        ? 'margin-top: auto'
        : '';
    },
    buttonBackgroundColor() {
      return (
        'linear-gradient(90deg,' + this.buttonBackgroundColor1 + ' , ' + this.buttonBackgroundColor2
      );
    },
    buttonHasGradient() {
      return (
        this.buttonBackgroundColor1 &&
        this.buttonBackgroundColor2 &&
        !this.buttonBackgroundTransparent
      );
    },
    buttonProps() {
      const props = this.buttonHasGradient
        ? {
            gradient: this.buttonBackgroundColor,
            withGradient: true
          }
        : this.buttonBackgroundTransparent
        ? {
            shape: 'outline',
            color: 'transparent'
          }
        : {};
      props.fontColor = this.buttonTextColor;
      return props;
    },
    isGif() {
      return this.imageUrl.pathname.includes('.gif');
    }
  },
  created() {
    const { primary } = this.slice;

    this.buttonBlank = primary.button_blank;
    this.buttonPath = primary.button_path[0]?.text;
    this.buttonText = primary.button_text[0]?.text;
    this.buttonBackgroundTransparent = primary.button_background_transparent;
    this.buttonTextColor = primary.button_text_color;
    this.buttonSize = primary.button_size;
    this.buttonBackgroundColor1 = primary.button_background_color1;
    this.buttonBackgroundColor2 = primary.button_background_color2;
    if (primary.image?.url) {
      this.imageAlt = primary.image.alt;
      this.imageUrl = new URL(primary.image.url);
      this.imageUrl.searchParams.set('auto', 'format');
    }
    this.backgroundImageUrl = primary.background_image.url;
    this.mobileBackgroundImageUrl = primary.mobile_background_image?.url;
    this.openModal = primary.open_modal;
    this.text = primary.text[0]?.text ? primary.text : '';
    this.endDate = primary.end_date;
    if (this.text) {
      this.textParsed = [];
      this.textParsed[0] = { ...this.text[0] };
    }
    this.title = primary.title1[0]?.text ? primary.title1 : '';
    this.textColor = primary.text_color;
    this.fullWidth = primary.full_width;
    this.fullViewport = primary.full_viewport;
    this.withShadow = primary.with_shadow;
    this.videoPath = primary.video_path;
    this.videoIsEmbedded = primary.video_is_embedded;

    this.borderRounded = primary.border_rounded;
    this.internalPadding = primary.internal_padding;
    this.externalMargin = primary.external_margin;
    this.anchorIdTarget = this.slice.primary.anchor_id_target;

    const videoPoster = primary.video_poster;

    if (this.videoIsEmbedded) {
      this.options = {
        language: 'en',
        playbackRates: [1.0],
        sources: [
          {
            type: 'video/mp4',
            src: this.videoPath
          }
        ],
        poster: videoPoster ? videoPoster : ''
      };
      this.videoPath = '';
    }
    this.imageVerticalAlign = primary.image_vertical_align;
    if (this.endDate) {
      const formatedDate = this.endDate.slice(0, this.endDate.length - 5);
      const endDate = new Date(formatedDate);
      const diff = endDate.getTime() - new Date().getTime();
      this.timeDiff = diff > 0 ? diff : 0;
    }
  },
  mounted() {
    if (this.timeDiff) {
      this.startInterval();
    } else if (this.endDate) {
      this.timeRemaining = '00:00:00';
    }
  },
  beforeDestroy() {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
  },
  methods: {
    handleClick(clickUrl) {
      this.sendGtmEvent(clickUrl);
    },
    sendGtmEvent(clickUrl) {
      this.$dataLayerPush('left_right_slice_button_click', {
        leftRightSlice: {
          clickUrl: clickUrl
        }
      });
    },
    startInterval() {
      this.timerId = setInterval(() => {
        this.timeDiff = this.timeDiff - 1000;
        this.timeRemaining = msToHMS(this.timeDiff);
      }, 1000);
    }
  }
};
</script>

<style lang="scss" scoped>
.image-container {
  background-image: var(--background-img);
  @include sm {
    background-image: var(--mobile-img);
  }
}
.reverse-row {
  flex-direction: row-reverse;
}
.image {
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.title {
  text-align: left;
  font-size: 32px;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0px;
  opacity: 1;
  padding-bottom: 20px;
}
.container-shadow {
  box-shadow: 0px 9px 25px #00000029;
}
.full-viewport {
  margin-left: -23px;
  margin-right: -23px;
}
.external-margin {
  margin: 5px 25px;
}
.auto-margin {
  margin: 0 auto;
}
.text {
  text-align: left;
  font-size: 16px;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0px;
  opacity: 1;
  padding-bottom: 20px;
}

.text-container {
  display: flex;
  align-items: center;
  @include lg {
    flex-basis: auto;
  }
}

.text-content {
  padding: 20px;
}

.counter-slice-seasonal {
  font-size: 1.9em;
}
</style>

<style>
.text-m-0 p {
  margin: 0px;
}
</style>
